import React, { useEffect, useMemo, useState } from "react";
import { bracketScheduleFor, bracketSeasonYear } from "./bracketSchedules";
import { ConferenceLink } from "./ConferenceLink";
import { rankConference } from "./conferenceRank";
import { hasResults } from "./records";
import { TournamentBracket } from "./TournamentBracket";
import { canProjectBracket, type SeedTeam } from "./tournament";
import { SortHeader, type SortSpec, toggleSort } from "./SortHeader";
import { TeamLink } from "./TeamLink";
import { TeamLogo } from "./TeamLogo";
import { useFetch } from "./useFetch";
import type {
  ConferencesResponse,
  Standing,
  StandingsResponse,
  TeamsResponse,
  WLT,
} from "./types";
import type { StandingsMode } from "./useURLState";

// Which record flavor the Record column displays. Driven by the
// Overall / Conference / Non-conference toggle; the other columns
// (Road / Neutral / Home / Non-D1 / RPI) do not change, though Neutral and
// Non-D1 hide when every row is 0-0-0.
const MODES: { key: StandingsMode; label: string }[] = [
  { key: "overall", label: "Overall" },
  { key: "conference", label: "Conference" },
  { key: "non-conference", label: "Non-conference" },
];

// pickRecord returns the W-L-T for the active mode. Overall falls
// back to the top-level Wins/Losses/Ties so pre-#59 responses render
// a zero-filled Overall column rather than a crash.
function pickRecord(s: Standing, mode: StandingsMode): WLT {
  switch (mode) {
    case "conference":
      return s.conferenceRecord;
    case "non-conference":
      return s.nonConferenceRecord;
    default:
      return { wins: s.wins, losses: s.losses, ties: s.ties };
  }
}

const SPORT_LABEL: Record<string, string> = {
  "soccer-women": "Women's Soccer",
  "soccer-men": "Men's Soccer",
  football: "Football",
  "basketball-women": "Women's Basketball",
  "basketball-men": "Men's Basketball",
};

// Scoreboard emits hyphen slugs; catalog emits underscore slugs. The
// standings service persists whatever ingest wrote. Resolve both forms
// against the catalog's team list so the RPI table shows real names
// regardless of which side wrote the row.
function buildNameLookup(teams: TeamsResponse | null): Map<string, string> {
  const m = new Map<string, string>();
  if (!teams) return m;
  for (const t of teams.teams) {
    m.set(t.slug, t.shortName);
    m.set(t.slug.replace(/_/g, "-"), t.shortName);
  }
  return m;
}

function titleCase(slug: string): string {
  return slug
    .split(/[-_]/)
    .filter((p) => p.length > 0)
    .map((p) => p[0].toUpperCase() + p.slice(1))
    .join(" ");
}

function wlt(r: WLT): string {
  return `${r.wins}-${r.losses}-${r.ties}`;
}

type Key =
  | "confRank"
  | "rank"
  | "teamSlug"
  | "games"
  | "winPercentage"
  | "owp"
  | "oowp"
  | "rpi";

// Row-level projection: Standing augmented with a client-computed
// confRank. Position within the team's own conference by points, then
// that conference's tiebreak procedure (see conferenceRank.ts).
// RPI is deliberately NOT a tiebreaker: conferences rank by their
// own in-conference record, not by a cross-divisional metric.
// Kept out of the wire type: the standings service shouldn't
// publish confRank — it falls out of the full-division result
// trivially on the client.
type RowView = Standing & { confRank: number };

const DEFAULT_DIR: Record<Key, "asc" | "desc"> = {
  confRank: "asc",
  rank: "asc",
  teamSlug: "asc",
  games: "desc",
  winPercentage: "desc",
  owp: "desc",
  oowp: "desc",
  rpi: "desc",
};

// defaultSortFor picks the natural sort when the user hasn't clicked
// a header yet. With a conference filter active the user is scoped
// to one conference and Conf Rank 1..N is the obvious ordering;
// without a filter the full-division view sorts by overall RPI rank.
function defaultSortFor(conference: string): SortSpec<Key> {
  return conference
    ? { key: "confRank", dir: "asc" }
    : { key: "rpi", dir: "desc" };
}

export type RPIPageProps = {
  sport: string;
  division: string;
  mode: StandingsMode;
  onModeChange: (m: StandingsMode) => void;
  conference: string;
  onConferenceChange: (slug: string) => void;
};

export function RPIPage({
  sport,
  division,
  mode,
  onModeChange,
  conference,
  onConferenceChange,
}: RPIPageProps) {
  const [sort, setSort] = useState<SortSpec<Key>>(() =>
    defaultSortFor(conference),
  );

  // When the conference filter flips the natural ordering flips too:
  // narrowing to one conference → sort by Conf Rank; clearing →
  // sort by RPI Rank. Overwrites any user-selected sort because the
  // user's intent in picking a conference is to see that conference
  // in its own ordering.
  useEffect(() => {
    setSort(defaultSortFor(conference));
  }, [conference]);

  const state = useFetch<StandingsResponse>(
    `/api/v1/standings?sport=${encodeURIComponent(sport)}&division=${encodeURIComponent(division)}`,
  );
  const teamsState = useFetch<TeamsResponse>(
    `/api/v1/teams?sport=${encodeURIComponent(sport)}&division=${encodeURIComponent(division)}`,
  );
  const confsState = useFetch<ConferencesResponse>("/api/v1/conferences");

  const nameFor = useMemo(() => {
    const map = buildNameLookup(
      teamsState.kind === "ok" ? teamsState.data : null,
    );
    return (slug: string): string => map.get(slug) ?? titleCase(slug);
  }, [teamsState]);

  // Build the conference option list from standings only — a
  // conference that has no teams in the current pool should not be
  // selectable, otherwise a share link into a stale filter renders
  // an empty table. Each option carries the display label; the
  // underlying slug drives the URL and the filter.
  const conferenceOptions = useMemo(() => {
    if (state.kind !== "ok") return [] as { slug: string; label: string }[];
    const labelBySlug = new Map<string, string>();
    if (confsState.kind === "ok") {
      for (const c of confsState.data.conferences) {
        labelBySlug.set(c.slug, c.shortName);
        labelBySlug.set(c.slug.replace(/_/g, "-"), c.shortName);
      }
    }
    const seen = new Map<string, string>();
    for (const s of state.data.standings) {
      if (!s.conference || seen.has(s.conference)) continue;
      seen.set(
        s.conference,
        s.conferenceLabel ??
          labelBySlug.get(s.conference) ??
          s.conference.toUpperCase().replace(/[-_]/g, " "),
      );
    }
    return Array.from(seen, ([slug, label]) => ({ slug, label })).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [state, confsState]);

  // confRankMap is keyed by teamSlug and holds each team's rank
  // within its own conference. See the RowView type comment for the
  // full tiebreaker chain. Built from the *full* standings list (not
  // the filtered rows) so clearing or re-applying the conference
  // filter does not reshuffle the within-conference ordering.
  const confRankMap = useMemo(() => {
    const m = new Map<string, number>();
    if (state.kind !== "ok") return m;
    const confGames = state.data.conferenceGames ?? [];
    const byConf = new Map<string, Standing[]>();
    for (const s of state.data.standings) {
      if (!s.conference) continue;
      const list = byConf.get(s.conference);
      if (list) list.push(s);
      else byConf.set(s.conference, [s]);
    }
    for (const list of byConf.values()) {
      rankConference(list, confGames).forEach((t, i) =>
        m.set(t.teamSlug, i + 1),
      );
    }
    return m;
  }, [state]);

  const sortedRows = useMemo<RowView[]>(() => {
    if (state.kind !== "ok") return [];
    let rows = state.data.standings;
    if (conference) {
      rows = rows.filter((s) => s.conference === conference);
    }
    // Augment each row with its within-conference rank so the sort
    // comparator can read confRank off the row itself and the cell
    // renderer doesn't have to re-key through the map.
    const view: RowView[] = rows.map((s) => ({
      ...s,
      confRank: confRankMap.get(s.teamSlug) ?? 0,
    }));
    return sortRows(view, sort);
  }, [state, sort, conference, confRankMap]);

  const activeConferenceLabel = useMemo(() => {
    if (!conference) return "";
    const hit = conferenceOptions.find((o) => o.slug === conference);
    return hit?.label ?? conference.toUpperCase().replace(/[-_]/g, " ");
  }, [conference, conferenceOptions]);

  // Tournament-cutoff divider: show it only when the user has filtered
  // to one conference AND the current sort orders teams by competitive
  // strength. Any other sort (team name, record) makes a "top N" line
  // misleading because the Nth row by name has nothing to do with
  // tournament qualification. The catalog's shape — both slug and
  // slug-with-hyphens — is checked because the standings `conference`
  // field drifts between the two forms.
  const activeTournamentBids = useMemo<number | undefined>(() => {
    if (!conference) return undefined;
    if (confsState.kind !== "ok") return undefined;
    const sortsByStrength =
      (sort.key === "confRank" && sort.dir === "asc") ||
      (sort.key === "rank" && sort.dir === "asc") ||
      (sort.key === "rpi" && sort.dir === "desc");
    if (!sortsByStrength) return undefined;
    const match = confsState.data.conferences.find(
      (c) =>
        c.slug === conference ||
        c.slug.replace(/_/g, "-") === conference ||
        c.slug.replace(/-/g, "_") === conference,
    );
    const bids = match?.tournamentBids;
    return bids && bids > 0 ? bids : undefined;
  }, [conference, confsState, sort]);

  return (
    <section>
      <div className="page-head">
        <h2>
          Standings ·{" "}
          <span className="muted">
            {division.toUpperCase()} {SPORT_LABEL[sport] ?? sport}
            {conference ? ` · ${activeConferenceLabel}` : ""}
          </span>
        </h2>
        {state.kind === "ok" && (
          <span
            className="count-badge"
            role="status"
            aria-label={`${sortedRows.length} ${sortedRows.length === 1 ? "team" : "teams"} in the current view`}
          >
            {sortedRows.length}{" "}
            {sortedRows.length === 1 ? "team" : "teams"}
          </span>
        )}
      </div>

      <div className="controls">
        <div className="mode-toggle" role="group" aria-label="Record mode">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              aria-pressed={mode === m.key}
              className={mode === m.key ? "active" : undefined}
              onClick={() => {
                onModeChange(m.key);
                // Overall means "show the full division", so clear any
                // conference filter in the same click rather than
                // leaving an active filter chip + Overall mode in a
                // contradictory state.
                if (m.key === "overall" && conference) {
                  onConferenceChange("");
                }
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
        <label
          className={
            conference ? "header-select is-active" : "header-select"
          }
        >
          <span className="visually-hidden">Conference</span>
          <select
            value={conference}
            onChange={(e) => onConferenceChange(e.target.value)}
            aria-label="Conference"
          >
            <option value="">All conferences</option>
            {conferenceOptions.map((o) => (
              <option key={o.slug} value={o.slug}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Filter-active badge: surfaces the current filter so a
          returning user (filter persisted from a prior visit) sees
          what's narrowing the view, and gives a one-click clear. */}
      {conference && (
        <div className="filter-badge" role="status">
          <span>
            Filtering by <strong>{activeConferenceLabel}</strong>
          </span>
          <button
            type="button"
            className="filter-badge-clear"
            onClick={() => onConferenceChange("")}
            aria-label={`Clear ${activeConferenceLabel} filter`}
          >
            Clear ×
          </button>
        </div>
      )}

      {state.kind === "loading" && <p role="status">Loading…</p>}
      {state.kind === "error" && (
        <p role="alert">Failed to load standings: {state.message}</p>
      )}
      {state.kind === "ok" &&
        (sortedRows.length === 0 ? (
          <div className="empty-state" role="status">
            <div className="empty-state-headline">
              {conference ? "No teams match" : "No standings yet"}
            </div>
            <div className="empty-state-detail">
              {conference
                ? `${activeConferenceLabel} has no teams in ${division.toUpperCase()} ${SPORT_LABEL[sport] ?? sport}.`
                : "Standings appear once games are recorded for the season."}
            </div>
            {conference && (
              <button
                type="button"
                className="empty-state-action"
                onClick={() => onConferenceChange("")}
              >
                Clear filter
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <StandingsTable
                rows={sortedRows}
                sort={sort}
                onSort={(k) => setSort(toggleSort(sort, k, DEFAULT_DIR[k]))}
                nameFor={nameFor}
                mode={mode}
                sport={sport}
                division={division}
                conferenceFilter={conference}
                tournamentBids={activeTournamentBids}
              />
            </div>
            {activeTournamentBids !== undefined && canProjectBracket(conference) && (
              <TournamentBracket
                bids={activeTournamentBids}
                teams={buildSeedTeams(sortedRows, activeTournamentBids, nameFor)}
                sport={sport}
                division={division}
                conferenceLabel={activeConferenceLabel}
                schedule={bracketScheduleFor(conference, bracketSeasonYear())}
              />
            )}
          </>
        ))}
    </section>
  );
}

function StandingsTable({
  rows,
  sort,
  onSort,
  nameFor,
  mode,
  sport,
  division,
  conferenceFilter,
  tournamentBids,
}: {
  rows: RowView[];
  sort: SortSpec<Key>;
  onSort: (k: Key) => void;
  nameFor: (slug: string) => string;
  mode: StandingsMode;
  sport: string;
  division: string;
  // Active conference-filter slug (empty when all conferences are
  // shown). Drives two column changes:
  //   - the Conf column hides (same value repeats on every row)
  //   - a Pts column appears left of the record column (3W + 1T
  //     only makes sense when every row's record is from the same
  //     conference's play).
  conferenceFilter: string;
  // Row index after which to render a conference-tournament cutoff
  // divider. Undefined when the active view can't anchor one (no
  // conference filter, no seeded bid count, or a sort that doesn't
  // order teams by competitive strength).
  tournamentBids?: number;
}) {
  const conferenceActive = Boolean(conferenceFilter);
  const recordLabel =
    mode === "conference"
      ? "Conf"
      : mode === "non-conference"
        ? "Non-Conf"
        : "Record";
  const showNeutral = hasResults(rows, (s) => s.neutralRecord);
  const showNonDivision = hasResults(rows, (s) => s.nonDivisionRecord);
  // Rank, RPI rank, team, conf or points, record, road, home, RPI.
  const columnCount = 8 + (showNeutral ? 1 : 0) + (showNonDivision ? 1 : 0);
  return (
    <table className="standings">
      <thead>
        <tr>
          <SortHeader
            label="Conf Rank"
            sortKey="confRank"
            sort={sort}
            onToggle={onSort}
            align="right"
            title="Rank within the team's conference by RPI"
          />
          <SortHeader
            label="RPI Rank"
            sortKey="rank"
            sort={sort}
            onToggle={onSort}
            align="right"
            title="Overall rank across the division by RPI"
          />
          <SortHeader
            label="Team"
            sortKey="teamSlug"
            sort={sort}
            onToggle={onSort}
          />
          {!conferenceActive && <th title="Conference">Conf</th>}
          {conferenceActive && (
            <th
              title="Conference points: 3 for a win, 1 for a draw, 0 for a loss"
              className="num"
            >
              Pts
            </th>
          )}
          <th
            title={
              mode === "conference"
                ? "Conference record (W-L-T)"
                : mode === "non-conference"
                  ? "Non-conference record (W-L-T)"
                  : "Overall record (W-L-T)"
            }
            className="num"
          >
            {recordLabel}
          </th>
          <th title="Road record (W-L-T)" className="num">
            Road
          </th>
          {showNeutral && (
            <th title="Neutral-site record (W-L-T)" className="num">
              Neutral
            </th>
          )}
          <th title="Home record (W-L-T)" className="num">
            Home
          </th>
          {showNonDivision && (
            <th title="Record vs non-Division-I opponents (W-L-T)" className="num">
              Non-D1
            </th>
          )}
          <SortHeader
            label="RPI"
            sortKey="rpi"
            sort={sort}
            onToggle={onSort}
            align="right"
            title="0.25·WP + 0.5·OWP + 0.25·OOWP"
          />
        </tr>
      </thead>
      <tbody>
        {rows.map((s, i) => (
          <React.Fragment key={s.teamSlug}>
          <tr>
            <td className="num">{s.confRank > 0 ? s.confRank : "—"}</td>
            <td className="num">{s.rank}</td>
            <td className="team-with-logo">
              <TeamLink
                slug={s.teamSlug}
                sport={sport}
                division={division}
                title={`Open ${nameFor(s.teamSlug)} team page`}
              >
                <TeamLogo slug={s.teamSlug} alt={nameFor(s.teamSlug)} />
                {nameFor(s.teamSlug)}
              </TeamLink>
            </td>
            {!conferenceActive && (
              <td className="muted">
                {s.conference ? (
                  <ConferenceLink
                    conference={s.conference}
                    sport={sport}
                    division={division}
                    label={s.conferenceLabel ?? s.conference}
                    title={`Show ${s.conferenceLabel ?? s.conference} standings`}
                  />
                ) : (
                  "—"
                )}
              </td>
            )}
            {conferenceActive && (
              <td className="num">
                {s.conferenceRecord.wins * 3 + s.conferenceRecord.ties}
              </td>
            )}
            <td className="num">{wlt(pickRecord(s, mode))}</td>
            <td className="num">{wlt(s.roadRecord)}</td>
            {showNeutral && <td className="num">{wlt(s.neutralRecord)}</td>}
            <td className="num">{wlt(s.homeRecord)}</td>
            {showNonDivision && <td className="num">{wlt(s.nonDivisionRecord)}</td>}
            <td className="num">{s.rpi.toFixed(4)}</td>
          </tr>
          {tournamentBids !== undefined && i + 1 === tournamentBids && i + 1 < rows.length && (
            <tr
              className="cutoff-row"
              aria-label={`Tournament cutoff — top ${tournamentBids} teams qualify`}
            >
              <td colSpan={columnCount}>
                <span className="cutoff-label">
                  Top {tournamentBids} — conference tournament cutoff
                </span>
              </td>
            </tr>
          )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
}

// buildSeedTeams takes the top `bids` rows from a competitively-sorted
// standings view and maps them onto the bracket builder's input shape.
// Callers must only invoke this when activeTournamentBids is defined
// (ensures the sort is by competitive strength) — otherwise seed 1
// might be a team sorted alphabetically.
function buildSeedTeams(
  rows: RowView[],
  bids: number,
  nameFor: (slug: string) => string,
): SeedTeam[] {
  return rows.slice(0, bids).map((r) => ({
    teamSlug: r.teamSlug,
    shortName: nameFor(r.teamSlug),
  }));
}

function sortRows(rows: RowView[], sort: SortSpec<Key>): RowView[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  const key = sort.key;
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (typeof av === "number" && typeof bv === "number") {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv)) * dir;
  });
}
